import React, { useEffect, useRef, useState } from 'react'
import { RefCallBack } from 'react-hook-form'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Input, InputRef, Text } from '@components/base'

interface AddressAutoCompleteProps {
  onChange: (text: string) => void
  onBlur?: () => void
  options: { text: string; value: string }[]
  value: string
  onSelect?: (idx: number) => void
  isInvalid: boolean
  label: string
  placeholder: string
  fieldRef: RefCallBack
}

export const AddressAutoComplete = ({
  onChange,
  onSelect,
  onBlur,
  options,
  value,
  isInvalid,
  label,
  fieldRef,
  placeholder,
}: AddressAutoCompleteProps) => {
  const styles = useStyleSheet(themedStyle)
  const inputRef = useRef<InputRef>(null)
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    return () => {
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current)
      }
    }
  }, [])

  const showOptions = isFocused && options.length > 0

  const clearBlurTimeout = () => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current)
      blurTimeout.current = null
    }
  }

  return (
    <View>
      <Input
        ref={(e) => {
          if (typeof fieldRef === 'function') {
            fieldRef(e)
          }
          inputRef.current = e
        }}
        placeholder={placeholder}
        value={value}
        label={label}
        hasError={isInvalid}
        onFocus={() => {
          clearBlurTimeout()
          setIsFocused(true)
        }}
        onChangeText={onChange}
        onBlur={() => {
          clearBlurTimeout()
          blurTimeout.current = setTimeout(() => {
            setIsFocused(false)
            onBlur?.()
            blurTimeout.current = null
          }, 150)
        }}
      />
      {showOptions && (
        <View style={styles.optionsContainer}>
          <ScrollView
            bounces={false}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
            style={styles.optionsScroll}
          >
            {options.map((option, idx) => (
              <TouchableOpacity
                key={option.value}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={option.text}
                style={[styles.option, idx === options.length - 1 && styles.optionLast]}
                onPress={() => {
                  clearBlurTimeout()
                  setIsFocused(false)
                  inputRef.current?.blur()
                  if (onSelect) {
                    onSelect(idx)
                  } else {
                    onChange(option.value)
                  }
                }}
              >
                <Text type="regular">{option.text}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  )
}

const themedStyle = StyleService.create({
  optionsContainer: {
    marginTop: 8,
    borderWidth: 2,
    borderColor: 'theme.surface.base',
    borderRadius: 8,
    backgroundColor: 'theme.background',
    overflow: 'hidden',
  },
  optionsScroll: {
    maxHeight: 192,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'theme.surface.base',
  },
  optionLast: {
    borderBottomWidth: 0,
  },
})
